import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import PrivateRoute from "./auth/PrivateRoute";
import Login from "./auth/Login";
import ValidarEmail from "./auth/ValidarEmail";
import RestablecerPassword from "./auth/RestablecerPassword";
import Layout from "./components/Layout";
import { rutaInicio } from "./utils/rutaInicio";

import ClienteDashboard from "./modules/dashboard/ClienteDashboard";
import AdminDashboard from "./modules/dashboard/AdminDashboard";
import CuentasBancarias from "./modules/cuentas/CuentasBancarias";
import TarjetasCredito from "./modules/cuentas/TarjetasCredito";
import GastosPage from "./modules/gastos/GastosPage";
import Ingresos from "./modules/ingresos/Ingresos";
import FamiliaGastosPage from "./modules/familia/FamiliaGastosPage";
import MiFamilia from "./modules/familia/MiFamilia";
import ResolverSolicitud from "./modules/familia/ResolverSolicitud";
import ResolverInvitacion from "./modules/familia/ResolverInvitacion";
import PrimerIngresoFamilia from "./modules/familia/PrimerIngresoFamilia";
import CobrosFamiliaPage from "./modules/familia/CobrosFamiliaPage";
import PagosExtraPage from "./modules/pagosExtra/PagosExtraPage";
import HistoricosPage from "./modules/historicos/HistoricosPage";

import Usuarios from "./modules/mantenimiento/Usuarios";
import Bancos from "./modules/mantenimiento/Bancos";
import MonedasMant from "./modules/mantenimiento/MonedasMant";
import TiposCambioMant from "./modules/mantenimiento/TiposCambioMant";
import AccesosMant from "./modules/mantenimiento/AccesosMant";
import Configuracion from "./modules/mantenimiento/Configuracion";

function DashboardRouter() {
  const { esAdministrador } = useAuth();
  return esAdministrador ? <AdminDashboard /> : <ClienteDashboard />;
}

// "/" y cualquier ruta desconocida: cada perfil va a su pantalla de inicio (el Administrador, a Mantenimiento).
function RedirigirInicio() {
  const { usuario } = useAuth();
  return <Navigate to={rutaInicio(usuario)} replace />;
}

// El gate de primer ingreso no debe interceptar las rutas donde el propio Cliente necesita
// resolver algo de familia (Mi Familia, o un enlace de correo de solicitud/invitación) --
// si no, alguien invitado directo (que nunca decidió antes) quedaría atrapado sin poder
// aceptar su propia invitación.
const RUTAS_EXENTAS_GATE_FAMILIA = [/^\/mi-familia$/, /^\/familia\/solicitudes\//, /^\/familia\/invitacion\//];

function ConLayout({ children }) {
  const { usuario } = useAuth();
  const location = useLocation();
  const exento = RUTAS_EXENTAS_GATE_FAMILIA.some((r) => r.test(location.pathname));
  const debeDecidirFamilia = usuario?.perfil === "Cliente" && !usuario?.familiaDecisionTomada && !exento;
  return <Layout>{debeDecidirFamilia ? <PrimerIngresoFamilia /> : children}</Layout>;
}

export default function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/validar-email" element={<ValidarEmail />} />
          <Route path="/restablecer-password" element={<RestablecerPassword />} />

          {/* Dashboard, Cuentas y Tarjetas: solo Cliente -- el Administrador trabaja únicamente en Mantenimiento */}
          <Route path="/dashboard" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><DashboardRouter /></ConLayout></PrivateRoute>
          } />

          <Route path="/cuentas" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><CuentasBancarias /></ConLayout></PrivateRoute>
          } />

          <Route path="/tarjetas-credito" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><TarjetasCredito /></ConLayout></PrivateRoute>
          } />

          <Route path="/gastos" element={
            <PrivateRoute><ConLayout><GastosPage /></ConLayout></PrivateRoute>
          } />

          <Route path="/ingresos" element={
            <PrivateRoute><ConLayout><Ingresos /></ConLayout></PrivateRoute>
          } />

          <Route path="/familia-gastos" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><FamiliaGastosPage /></ConLayout></PrivateRoute>
          } />

          <Route path="/mi-familia" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><MiFamilia /></ConLayout></PrivateRoute>
          } />
          <Route path="/familia/solicitudes/:token" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><ResolverSolicitud /></ConLayout></PrivateRoute>
          } />
          <Route path="/familia/invitacion/:token" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><ResolverInvitacion /></ConLayout></PrivateRoute>
          } />

          <Route path="/cobros-familia" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><CobrosFamiliaPage /></ConLayout></PrivateRoute>
          } />

          <Route path="/pagos-extra" element={
            <PrivateRoute roles={["Cliente"]} requiere={(u) => u.accesoPagosExtra}><ConLayout><PagosExtraPage /></ConLayout></PrivateRoute>
          } />

          <Route path="/historicos" element={
            <PrivateRoute roles={["Cliente"]}><ConLayout><HistoricosPage /></ConLayout></PrivateRoute>
          } />

          {/* Mantenimiento: exclusivo del perfil Administrador */}
          <Route path="/mant/usuarios" element={
            <PrivateRoute roles={["Administrador"]}><ConLayout><Usuarios /></ConLayout></PrivateRoute>
          } />
          <Route path="/mant/bancos" element={
            <PrivateRoute roles={["Administrador"]}><ConLayout><Bancos /></ConLayout></PrivateRoute>
          } />
          <Route path="/mant/monedas" element={
            <PrivateRoute roles={["Administrador"]}><ConLayout><MonedasMant /></ConLayout></PrivateRoute>
          } />
          <Route path="/mant/tipocambio" element={
            <PrivateRoute roles={["Administrador"]}><ConLayout><TiposCambioMant /></ConLayout></PrivateRoute>
          } />
          <Route path="/mant/accesos" element={
            <PrivateRoute roles={["Administrador"]}><ConLayout><AccesosMant /></ConLayout></PrivateRoute>
          } />
          <Route path="/mant/configuracion" element={
            <PrivateRoute roles={["Administrador"]}><ConLayout><Configuracion /></ConLayout></PrivateRoute>
          } />

          <Route path="/" element={<RedirigirInicio />} />
          <Route path="*" element={<RedirigirInicio />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}
